import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import { pullExpensesWhenNeeded } from '../../../sync/core/pullExpensesWhenNeeded';
import { getSyncTimestamps } from '../../../sync/core/syncTimestamps';
import { useDebouncedValue } from '../../../shared/ui/hooks-ui/useDebouncedValue';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';
import { defaultExpensePeriodFrom, todayIsoDate, resolvePeriodDates, localCalendarDayIsoRange } from '../../infra/expenseListSql'
import { listBranchSettlementsLocal } from '../../infra/expensesRepoSettlementsLocal'
import type { ExpenseSettlementListRow, ExpenseSettlementMethod, ExpenseSettlementsListQuery } from '../../domain/expenseTypes';
import {
  EXPENSE_PERIOD_PRESETS,
  EXPENSES_PAYMENTS_PAGE_SIZE_STORAGE_KEY,
  type ExpensePeriodPreset,
} from '../../application/expensesList.constants';
import { formatExpenseDate } from '../../domain/expenseLabels';
import { usePaginatedRemoteList } from '../../../shared/data/usePaginatedRemoteList';

export type ExpensePaymentsStatusFilter = 'active' | 'voided' | 'all';

const STALE_PULL_MS = 6 * 60 * 60 * 1000;

type PaymentsQuery = Omit<ExpenseSettlementsListQuery, 'limit' | 'offset'>;

export function useExpensePaymentsHistory(branchId: string | null | undefined, enabled: boolean) {
  const [periodPreset, setPeriodPreset] = useState<ExpensePeriodPreset>('30d');
  const [customFromDate, setCustomFromDate] = useState(defaultExpensePeriodFrom(30));
  const [customToDate, setCustomToDate] = useState(todayIsoDate());
  const [methodFilter, setMethodFilter] = useState<ExpenseSettlementMethod | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ExpensePaymentsStatusFilter>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounced = useDebouncedValue(searchQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const [staleWarning, setStaleWarning] = useState(false);
  const [pullError, setPullError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const periodDates = useMemo(
    () => resolvePeriodDates(periodPreset, customFromDate, customToDate),
    [periodPreset, customFromDate, customToDate]
  );

  const paymentsQuery = useMemo<PaymentsQuery>(() => {
    const { fromIso, toIso } = localCalendarDayIsoRange(periodDates.fromDate, periodDates.toDate);
    return {
      branchId: branchId ?? '',
      fromIso,
      toIso,
      method: methodFilter === 'all' ? null : methodFilter,
      status: statusFilter,
      search: searchDebounced || null,
    };
  }, [branchId, periodDates, methodFilter, statusFilter, searchDebounced]);

  const criteriaKey = useMemo(
    () =>
      `${paymentsQuery.branchId}|${paymentsQuery.fromIso}|${paymentsQuery.toIso}|${methodFilter}|${statusFilter}|${searchDebounced}`,
    [paymentsQuery, methodFilter, statusFilter, searchDebounced]
  );

  const fetchPage = useCallback(
    ({ limit, offset, query }: { limit: number; offset: number; query: PaymentsQuery }) =>
      listBranchSettlementsLocal({ ...query, limit, offset }).then((r) => r.rows),
    []
  );

  const count = useCallback(
    (query: PaymentsQuery) =>
      listBranchSettlementsLocal({ ...query, limit: 1, offset: 0 }).then((r) => r.total),
    []
  );

  const paging = usePaginatedRemoteList<ExpenseSettlementListRow, PaymentsQuery>({
    storageKey: EXPENSES_PAYMENTS_PAGE_SIZE_STORAGE_KEY,
    criteriaKey,
    query: paymentsQuery,
    enabled: enabled && branchId != null,
    logLabel: 'ExpensePayments',
    fetchPage,
    count,
  });

  const refreshStaleFlag = useCallback(async () => {
    if (!branchId) return;
    const online = await isOnline();
    if (!online) {
      const ts = await getSyncTimestamps();
      const last = ts.expensesLastPullAt ? Date.parse(ts.expensesLastPullAt) : 0;
      setStaleWarning(!last || Date.now() - last > STALE_PULL_MS);
      return;
    }
    setStaleWarning(false);
  }, [branchId]);

  // El aviso de "pagos posiblemente viejos" se recalcula con cada criterio nuevo.
  useEffect(() => {
    if (!enabled || !branchId) return;
    void refreshStaleFlag();
  }, [enabled, branchId, criteriaKey, refreshStaleFlag]);

  const reload = useCallback(async (): Promise<{ pullError?: string | null }> => {
    if (!enabled || !branchId) return {};
    let pullErrorResult: string | null = null;
    try {
      const pull = await pullExpensesWhenNeeded(branchId, { force: true });
      if (!mountedRef.current) return {};
      if (pull.error) {
        pullErrorResult = pull.error;
        setPullError(pull.error);
        setStaleWarning(true);
      } else {
        setPullError(null);
        setStaleWarning(false);
      }
      await paging.reloadFromStart();
      return { pullError: pullErrorResult };
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'expenses', op: 'payments_history.load', error: e });
      return { pullError: pullErrorResult };
    }
  }, [enabled, branchId, paging]);

  const hasActiveFilters =
    periodPreset !== '30d' ||
    methodFilter !== 'all' ||
    statusFilter !== 'active' ||
    searchDebounced.length > 0;

  const activeFilterCount = [
    periodPreset !== '30d',
    methodFilter !== 'all',
    statusFilter !== 'active',
    searchDebounced.length > 0,
  ].filter(Boolean).length;

  const clearFilters = useCallback(() => {
    setPeriodPreset('30d');
    setCustomFromDate(defaultExpensePeriodFrom(30));
    setCustomToDate(todayIsoDate());
    setMethodFilter('all');
    setStatusFilter('active');
    setSearchQuery('');
  }, []);

  return {
    periodPreset,
    setPeriodPreset,
    customFromDate,
    setCustomFromDate,
    customToDate,
    setCustomToDate,
    methodFilter,
    setMethodFilter,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    rows: paging.items,
    total: paging.totalCount,
    loading: paging.loading,
    error: paging.error,
    staleWarning,
    pullError,
    pageIndex: paging.pageIndex,
    pageSize: paging.pageSize,
    setPageSize: paging.setPageSize,
    hasMore: paging.hasMore,
    loadNextPage: paging.loadNextPage,
    loadPreviousPage: paging.loadPreviousPage,
    reload,
    periodLabel:
      periodPreset === 'custom'
        ? `${formatExpenseDate(periodDates.fromDate)} — ${formatExpenseDate(periodDates.toDate)}`
        : EXPENSE_PERIOD_PRESETS.find((p) => p.id === periodPreset)?.label ?? '30 días',
    hasActiveFilters,
    activeFilterCount,
    clearFilters,
    exportQuery: useMemo(() => {
      if (!branchId) return null;
      return paymentsQuery;
    }, [branchId, paymentsQuery]),
  };
}

export type ExpensePaymentsHistoryFlow = ReturnType<typeof useExpensePaymentsHistory>;
